<script setup lang="ts">
// Page V2 « Nouvelle demande » — création d'un dossier d'onboarding/offboarding.
// Le collaborateur EST un User existant : on le choisit via un sélecteur de
// recherche (API), plus de saisie libre. La dotation est pré-remplie selon le
// poste (jobTitle) de l'utilisateur sélectionné, puis ajustable.
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  InformationCircleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  UserPlusIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import type { ApiCollaboratorIdentity } from '@/api/materialApi'
import { ASSET_TYPES, CATEGORY_META, attributeLabel } from '@/v2/onboarding/catalog'
import { DEFAULT_TEMPLATE_CODES, templateForJob } from '@/v2/onboarding/templates'
import type { AssetCategory, CaseType, OwnedAsset } from '@/v2/onboarding/types'

const router = useRouter()
const store = useOnboardingStore()

// ── Type de dossier ────────────────────────────────────────────────────────────
const TYPE_OPTIONS: { value: CaseType; label: string; icon: typeof ArrowRightOnRectangleIcon }[] = [
  { value: 'ONBOARDING', label: 'Onboarding', icon: ArrowRightOnRectangleIcon },
  { value: 'OFFBOARDING', label: 'Offboarding', icon: ArrowLeftOnRectangleIcon },
]
const type = ref<CaseType>('ONBOARDING')

// ── Date pivot (arrivée pour onboarding, sortie pour offboarding) ───────────────
const pivotDate = ref('')
const isOnboarding = computed(() => type.value === 'ONBOARDING')
const dateFieldLabel = computed(() => (isOnboarding.value ? "Date d'arrivée" : 'Date de sortie'))

// ── Sélecteur de collaborateur (recherche d'un User existant) ───────────────────
const selectedUser = ref<ApiCollaboratorIdentity | null>(null)
const searchTerm = ref('')
const results = ref<ApiCollaboratorIdentity[]>([])
const searching = ref(false)
const searched = ref(false)

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let searchToken = 0

/** Nom affichable d'une identité (repli sur l'email puis l'uuid). */
function identityName(user: ApiCollaboratorIdentity): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.email || user.userUuid
}

async function runSearch(term: string): Promise<void> {
  const token = ++searchToken
  searching.value = true
  try {
    const rows = await store.searchUsers(term)
    if (token !== searchToken) return // réponse obsolète
    results.value = rows
  } catch {
    if (token !== searchToken) return
    results.value = []
  } finally {
    if (token === searchToken) {
      searching.value = false
      searched.value = true
    }
  }
}

// Debounce léger (~300 ms) sur la saisie de recherche.
watch(searchTerm, (value) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  const term = value.trim()
  if (!term) {
    results.value = []
    searched.value = false
    searching.value = false
    searchToken++
    return
  }
  debounceTimer = setTimeout(() => void runSearch(term), 300)
})

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

function selectUser(user: ApiCollaboratorIdentity): void {
  selectedUser.value = user
  results.value = []
  searched.value = false
  searchTerm.value = ''
}

/** Permet de revenir au sélecteur pour choisir un autre utilisateur. */
function changeUser(): void {
  selectedUser.value = null
}

// ── Dotation sélectionnée (Set de codes de types d'objets) ──────────────────────
// - ONBOARDING : codes cochés = dotation pré-remplie selon le poste, modifiable.
// - OFFBOARDING : codes cochés = uniquement les AJOUTS de matériel détenu non listé
//   (l'inventaire détenu est repris automatiquement côté back, non envoyé ici).
const selectedCodes = ref<Set<string>>(new Set(DEFAULT_TEMPLATE_CODES))
const prefillFromTemplate = ref(false)

function toggleCode(code: string, checked: boolean) {
  const next = new Set(selectedCodes.value)
  if (checked) next.add(code)
  else next.delete(code)
  selectedCodes.value = next
}

// ── Inventaire détenu (offboarding) ─────────────────────────────────────────────
const ownedAssets = ref<OwnedAsset[]>([])
const inventoryLoading = ref(false)
const inventoryLoaded = ref(false)
let inventoryToken = 0

/** Réinitialise l'inventaire détenu (changement d'utilisateur ou retour onboarding). */
function resetInventory() {
  inventoryToken++
  ownedAssets.value = []
  inventoryLoading.value = false
  inventoryLoaded.value = false
}

/** Charge l'inventaire détenu du collaborateur (anti-course via token). */
async function loadInventory(userUuid: string) {
  const token = ++inventoryToken
  inventoryLoading.value = true
  inventoryLoaded.value = false
  try {
    const owned = await store.fetchCollaboratorInventory(userUuid)
    if (token !== inventoryToken) return // réponse obsolète (user changé)
    ownedAssets.value = owned
  } catch {
    if (token !== inventoryToken) return
    ownedAssets.value = []
  } finally {
    if (token === inventoryToken) {
      inventoryLoading.value = false
      inventoryLoaded.value = true
    }
  }
}

/**
 * Applique l'état de dotation selon l'utilisateur + le type courant :
 * - onboarding → pré-remplissage par template + reset inventaire ;
 * - offboarding → aucun pré-cochage (ajouts seulement) + chargement inventaire.
 */
function syncSelectionForUser(user: ApiCollaboratorIdentity | null) {
  if (!user) {
    prefillFromTemplate.value = false
    selectedCodes.value = new Set()
    resetInventory()
    return
  }
  if (isOnboarding.value) {
    const template = templateForJob(user.jobTitle)
    const codes = template?.assetTypeCodes ?? DEFAULT_TEMPLATE_CODES
    selectedCodes.value = new Set(codes)
    prefillFromTemplate.value = Boolean(template)
    resetInventory()
  } else {
    // Offboarding : ne rien pré-cocher (l'inventaire détenu est repris auto).
    prefillFromTemplate.value = false
    selectedCodes.value = new Set()
    void loadInventory(user.userUuid)
  }
}

// Recalcule la dotation / l'inventaire quand l'utilisateur OU le type change.
watch(selectedUser, (user) => syncSelectionForUser(user))
watch(type, () => syncSelectionForUser(selectedUser.value))

// ── Types d'objets regroupés par catégorie ──────────────────────────────────────
const CATEGORY_ORDER: AssetCategory[] = ['IT', 'TELECOM', 'VEHICLE', 'ACCESS', 'OTHER']
const assetGroups = computed(() =>
  CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_META[category].label,
    types: ASSET_TYPES.filter((t) => t.category === category),
  })).filter((g) => g.types.length > 0),
)
const selectedCount = computed(() => selectedCodes.value.size)

// ── Inventaire détenu regroupé par catégorie (offboarding) ──────────────────────
const ownedGroups = computed(() =>
  CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_META[category].label,
    items: ownedAssets.value.filter((a) => a.category === category),
  })).filter((g) => g.items.length > 0),
)

/**
 * N° de série / IMEI d'un objet détenu (pour l'affichage en `tabular-nums`).
 * Renvoie « <Libellé> : <valeur> » ou une chaîne vide si aucun n'est renseigné.
 */
function ownedIdentifier(asset: OwnedAsset): string {
  const sn = asset.attributes.serialNumber
  const imei = asset.attributes.imei
  if (sn) return `${attributeLabel('serialNumber')} ${sn}`
  if (imei) return `${attributeLabel('imei')} ${imei}`
  return ''
}

// ── Commentaire ──────────────────────────────────────────────────────────────
const comment = ref('')

// ── Validation / soumission ──────────────────────────────────────────────────
const canSubmit = computed(() => selectedUser.value !== null)
const submitting = ref(false)

async function submit() {
  if (!selectedUser.value || submitting.value) return

  submitting.value = true
  try {
    const id = await store.createCase({
      type: type.value,
      userUuid: selectedUser.value.userUuid,
      arrivalDate: isOnboarding.value ? pivotDate.value || null : null,
      exitDate: isOnboarding.value ? null : pivotDate.value || null,
      comment: comment.value.trim() || undefined,
      assetTypeCodes: [...selectedCodes.value],
    })
    router.push(`/onboarding/${id}`)
  } finally {
    submitting.value = false
  }
}

function cancel() {
  router.push('/onboarding')
}
</script>

<template>
  <div class="mx-auto w-full max-w-5xl p-4 md:p-8">
    <!-- ── Fil d'Ariane ───────────────────────────────────────────────────────── -->
    <nav class="text-sm text-base-content/80" aria-label="Fil d'Ariane">
      <ol role="list" class="flex flex-wrap items-center gap-1">
        <li><RouterLink to="/" class="hover:underline">Hub Interne</RouterLink></li>
        <li aria-hidden="true">/</li>
        <li><RouterLink to="/onboarding" class="hover:underline">Onboarding</RouterLink></li>
        <li aria-hidden="true">/</li>
        <li class="text-base-content" aria-current="page">Nouvelle demande</li>
      </ol>
    </nav>

    <!-- ── En-tête ────────────────────────────────────────────────────────────── -->
    <header class="mt-3">
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <UserPlusIcon class="h-6 w-6 text-primary" aria-hidden="true" />
        Nouvelle demande
      </h1>
      <p class="mt-1 max-w-prose text-base-content/80">
        Création manuelle d'un dossier d'onboarding ou d'offboarding.
      </p>
    </header>

    <form class="mt-6 space-y-6" novalidate @submit.prevent="submit">
      <!-- ── Type de dossier ──────────────────────────────────────────────────── -->
      <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="type-title">
        <div class="card-body p-4 md:p-6">
          <h2 id="type-title" class="card-title text-lg font-semibold">Type de demande</h2>

          <div class="join mt-1" role="group" aria-label="Type de demande">
            <button
              v-for="opt in TYPE_OPTIONS"
              :key="opt.value"
              type="button"
              class="btn join-item gap-2"
              :class="type === opt.value ? 'btn-primary' : 'btn-ghost'"
              :aria-pressed="type === opt.value"
              @click="type = opt.value"
            >
              <component :is="opt.icon" class="h-5 w-5" aria-hidden="true" />
              {{ opt.label }}
            </button>
          </div>

          <div class="mt-4 max-w-xs">
            <label class="label" for="pivot-date">
              <span class="label-text">{{ dateFieldLabel }}</span>
            </label>
            <input
              id="pivot-date"
              v-model="pivotDate"
              type="date"
              class="input input-bordered w-full tabular-nums"
            />
          </div>
        </div>
      </section>

      <!-- ── Collaborateur (sélecteur d'utilisateur) ──────────────────────────── -->
      <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="collab-title">
        <div class="card-body p-4 md:p-6">
          <h2 id="collab-title" class="card-title text-lg font-semibold">Collaborateur</h2>

          <!-- Identité choisie (lecture seule) -->
          <div
            v-if="selectedUser"
            class="flex flex-wrap items-start justify-between gap-3 rounded-field border border-base-300 bg-base-200 p-4"
          >
            <div class="min-w-0">
              <p class="font-semibold break-words">{{ identityName(selectedUser) }}</p>
              <p class="text-sm break-words text-base-content/80">
                {{ selectedUser.email || '—' }}
                <span v-if="selectedUser.jobTitle"> · {{ selectedUser.jobTitle }}</span>
                <span v-if="selectedUser.entity"> · {{ selectedUser.entity }}</span>
              </p>
            </div>
            <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="changeUser">
              Changer
            </button>
          </div>

          <!-- Recherche d'un collaborateur -->
          <div v-else>
            <label class="label" for="collab-search">
              <span class="label-text">Rechercher un collaborateur</span>
            </label>
            <label class="input input-bordered flex items-center gap-2">
              <MagnifyingGlassIcon class="h-5 w-5 text-base-content/80" aria-hidden="true" />
              <input
                id="collab-search"
                v-model="searchTerm"
                type="search"
                class="grow"
                placeholder="Nom, prénom ou email…"
                autocomplete="off"
                aria-describedby="collab-search-hint"
              />
            </label>
            <p id="collab-search-hint" class="mt-1 text-sm text-base-content/80">
              Sélectionnez l'utilisateur concerné dans les résultats.
            </p>

            <!-- État chargement -->
            <p v-if="searching" class="mt-3 text-sm text-base-content/80" aria-live="polite">
              Recherche en cours…
            </p>

            <!-- Résultats -->
            <ul
              v-else-if="results.length"
              role="list"
              class="mt-3 divide-y divide-base-300 overflow-hidden rounded-field border border-base-300"
            >
              <li v-for="user in results" :key="user.userUuid">
                <button
                  type="button"
                  class="flex w-full flex-col items-start gap-0.5 p-3 text-left transition-colors hover:bg-base-200 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  @click="selectUser(user)"
                >
                  <span class="font-semibold break-words">{{ identityName(user) }}</span>
                  <span class="text-sm break-words text-base-content/80">
                    {{ user.email || '—' }}
                    <span v-if="user.jobTitle"> · {{ user.jobTitle }}</span>
                  </span>
                </button>
              </li>
            </ul>

            <!-- Aucun résultat -->
            <p
              v-else-if="searched && searchTerm.trim()"
              class="mt-3 text-sm text-base-content/80"
              aria-live="polite"
            >
              Aucun collaborateur ne correspond à la recherche.
            </p>
          </div>
        </div>
      </section>

      <!-- ── Dotation / équipements (ONBOARDING) ──────────────────────────────── -->
      <section
        v-if="isOnboarding"
        class="card border border-base-300 bg-base-100 shadow-sm"
        aria-labelledby="assets-title"
      >
        <div class="card-body p-4 md:p-6">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h2 id="assets-title" class="card-title text-lg font-semibold">Dotation / équipements</h2>
            <span class="badge badge-primary badge-outline tabular-nums">
              {{ selectedCount }} équipement{{ selectedCount > 1 ? 's' : '' }} sélectionné{{ selectedCount > 1 ? 's' : '' }}
            </span>
          </div>
          <p class="max-w-prose text-base-content/80">
            Cochez le matériel à doter. La sélection est pré-remplie selon le poste du collaborateur.
          </p>
          <p
            v-if="prefillFromTemplate"
            class="flex items-start gap-1 text-sm text-base-content/80"
          >
            <InformationCircleIcon class="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Dotation pré-remplie selon le poste — modifiable ci-dessous.</span>
          </p>

          <div class="mt-2 space-y-5">
            <fieldset v-for="group in assetGroups" :key="group.category">
              <legend class="mb-2 text-sm font-semibold uppercase tracking-wide text-base-content/80">
                {{ group.label }}
              </legend>
              <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label
                  v-for="asset in group.types"
                  :key="asset.code"
                  class="flex cursor-pointer items-center gap-3 rounded-field border border-base-300 p-3 transition-colors hover:bg-base-200 motion-reduce:transition-none"
                >
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary"
                    :checked="selectedCodes.has(asset.code)"
                    @change="toggleCode(asset.code, ($event.target as HTMLInputElement).checked)"
                  />
                  <span class="text-base">{{ asset.label }}</span>
                </label>
              </div>
            </fieldset>
          </div>
        </div>
      </section>

      <!-- ── Matériel détenu repris pour restitution (OFFBOARDING) ─────────────── -->
      <template v-else>
        <section
          class="card border border-base-300 bg-base-100 shadow-sm"
          aria-labelledby="owned-title"
        >
          <div class="card-body p-4 md:p-6">
            <h2 id="owned-title" class="flex items-center gap-2 card-title text-lg font-semibold">
              <ArrowUturnLeftIcon class="h-5 w-5 text-primary" aria-hidden="true" />
              Matériel détenu (repris pour restitution)
            </h2>
            <p class="flex items-start gap-1 text-sm text-base-content/80">
              <LockClosedIcon class="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Repris automatiquement pour la restitution — non modifiable.</span>
            </p>

            <!-- Aucun collaborateur encore choisi -->
            <p v-if="!selectedUser" class="mt-2 text-base-content/80">
              Sélectionnez un collaborateur pour afficher le matériel qu'il détient.
            </p>

            <!-- Chargement -->
            <p
              v-else-if="inventoryLoading"
              class="mt-2 text-sm text-base-content/80"
              aria-live="polite"
            >
              Chargement de l'inventaire détenu…
            </p>

            <!-- Inventaire vide -->
            <p
              v-else-if="inventoryLoaded && ownedAssets.length === 0"
              class="mt-2 text-base-content/80"
              aria-live="polite"
            >
              Aucun matériel détenu enregistré pour ce collaborateur.
            </p>

            <!-- Inventaire détenu (verrouillé) -->
            <div v-else-if="ownedAssets.length" class="mt-2 space-y-5">
              <fieldset v-for="group in ownedGroups" :key="group.category">
                <legend class="mb-2 text-sm font-semibold uppercase tracking-wide text-base-content/80">
                  {{ group.label }}
                </legend>
                <ul role="list" class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <li
                    v-for="asset in group.items"
                    :key="asset.id"
                    class="flex items-center gap-3 rounded-field border border-base-300 bg-base-200 p-3"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-primary"
                      checked
                      disabled
                      :aria-label="`${asset.label} — repris pour restitution`"
                    />
                    <span class="min-w-0">
                      <span class="block text-base">{{ asset.label }}</span>
                      <span
                        v-if="ownedIdentifier(asset)"
                        class="block text-sm tabular-nums text-base-content/80"
                      >
                        {{ ownedIdentifier(asset) }}
                      </span>
                    </span>
                  </li>
                </ul>
              </fieldset>
            </div>
          </div>
        </section>

        <!-- ── Ajouter du matériel détenu non listé (OFFBOARDING) ───────────────── -->
        <section
          class="card border border-base-300 bg-base-100 shadow-sm"
          aria-labelledby="extra-assets-title"
        >
          <div class="card-body p-4 md:p-6">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h2 id="extra-assets-title" class="flex items-center gap-2 card-title text-lg font-semibold">
                <PlusCircleIcon class="h-5 w-5 text-primary" aria-hidden="true" />
                Ajouter du matériel détenu non listé
              </h2>
              <span class="badge badge-primary badge-outline tabular-nums">
                {{ selectedCount }} ajout{{ selectedCount > 1 ? 's' : '' }}
              </span>
            </div>
            <p class="max-w-prose text-base-content/80">
              Cochez le matériel détenu mais non tracé pour qu'il soit ajouté à la restitution.
            </p>

            <div class="mt-2 space-y-5">
              <fieldset v-for="group in assetGroups" :key="group.category">
                <legend class="mb-2 text-sm font-semibold uppercase tracking-wide text-base-content/80">
                  {{ group.label }}
                </legend>
                <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label
                    v-for="asset in group.types"
                    :key="asset.code"
                    class="flex cursor-pointer items-center gap-3 rounded-field border border-base-300 p-3 transition-colors hover:bg-base-200 motion-reduce:transition-none"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-primary"
                      :checked="selectedCodes.has(asset.code)"
                      @change="toggleCode(asset.code, ($event.target as HTMLInputElement).checked)"
                    />
                    <span class="text-base">{{ asset.label }}</span>
                  </label>
                </div>
              </fieldset>
            </div>
          </div>
        </section>
      </template>

      <!-- ── Commentaire ──────────────────────────────────────────────────────── -->
      <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="comment-title">
        <div class="card-body p-4 md:p-6">
          <h2 id="comment-title" class="card-title text-lg font-semibold">Commentaire</h2>
          <label class="label" for="comment">
            <span class="label-text">Note interne (optionnelle)</span>
          </label>
          <textarea
            id="comment"
            v-model="comment"
            rows="3"
            class="textarea textarea-bordered w-full"
          ></textarea>
        </div>
      </section>

      <!-- ── Actions ──────────────────────────────────────────────────────────── -->
      <div class="flex flex-wrap items-center justify-end gap-3">
        <button type="button" class="btn btn-ghost" @click="cancel">Annuler</button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="!canSubmit || submitting"
          :aria-busy="submitting"
        >
          <CheckIcon class="h-5 w-5" aria-hidden="true" />
          Créer la demande
        </button>
      </div>
    </form>
  </div>
</template>
